import React, { useState, useEffect } from 'react';
import { getAvatarColor } from '@sahaj/utils';
import type { UserStatus } from '@sahaj/types';

export interface UserAvatarProps {
  userId?: string | undefined;
  displayName?: string | undefined;
  avatarUrl?: string | null | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showStatus?: boolean;
  status?: UserStatus | undefined;
  className?: string;
  alt?: string;
}

export const getInitials = (name?: string): string => {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0]?.charAt(0).toUpperCase() || '';
    const last = parts[parts.length - 1]?.charAt(0).toUpperCase() || '';
    return first + last;
  }
  return name.trim().charAt(0).toUpperCase();
};

const SIZE_CLASSES = {
  xs: 'w-6 h-6 text-[10px] rounded-lg',
  sm: 'w-8 h-8 text-xs rounded-xl',
  md: 'w-10 h-10 text-sm rounded-xl',
  lg: 'w-12 h-12 text-base rounded-2xl',
  xl: 'w-16 h-16 text-xl rounded-2xl',
  '2xl': 'w-20 h-20 text-3xl rounded-3xl',
};

const STATUS_SIZES = {
  xs: 'w-2 h-2',
  sm: 'w-2.5 h-2.5',
  md: 'w-3 h-3',
  lg: 'w-3.5 h-3.5',
  xl: 'w-4 h-4',
  '2xl': 'w-5 h-5',
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  displayName = 'User',
  avatarUrl,
  size = 'md',
  showStatus = false,
  status = 'ACTIVE',
  className = '',
  alt,
}) => {
  const [imgError, setImgError] = useState(false);

  // Reset imgError if avatarUrl changes
  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  const initials = getInitials(displayName);
  const bgColor = getAvatarColor(userId || displayName || 'user');
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const statusSize = STATUS_SIZES[size] || STATUS_SIZES.md;

  const hasValidImage = Boolean(avatarUrl && !imgError && avatarUrl.trim().length > 0);

  const statusColor =
    status === 'ACTIVE'
      ? 'bg-emerald-500'
      : status === 'AWAY'
      ? 'bg-amber-500'
      : status === 'DO_NOT_DISTURB'
      ? 'bg-rose-500'
      : 'bg-zinc-400';

  return (
    <div className={`relative inline-flex items-center justify-center shrink-0 ${sizeClass} ${className}`}>
      {hasValidImage ? (
        <img
          src={avatarUrl!}
          alt={alt || displayName || 'User avatar'}
          onError={() => setImgError(true)}
          className={`w-full h-full object-cover shadow-2xs ${sizeClass.split(' ').filter(c => c.startsWith('rounded-')).join(' ')}`}
          loading="lazy"
        />
      ) : (
        <div
          className={`w-full h-full flex items-center justify-center font-bold text-white shadow-2xs select-none ${sizeClass.split(' ').filter(c => c.startsWith('rounded-')).join(' ')}`}
          style={{ backgroundColor: bgColor }}
          title={displayName}
        >
          {initials}
        </div>
      )}

      {showStatus && (
        <span
          className={`absolute bottom-0 right-0 ${statusSize} ${statusColor} rounded-full border-2 border-background ring-1 ring-black/10`}
          title={`Status: ${status}`}
        />
      )}
    </div>
  );
};
